import {ContentContainer, ContentContainerProps} from '@coolcolduk/ui-mui';
import {Refresh} from '@mui/icons-material';
import {Button, CircularProgress, Stack, Typography} from '@mui/material';
import {PropsWithChildren, useEffect, useState} from 'react';
import {env} from '../../config/env';

export interface LoadingPageProps extends PropsWithChildren<ContentContainerProps> {
  onClick?: () => void;
  btnText?: string;
  message?: string;
  showButtonAfterMs?: number;
}

export default function LoadingPage({
  onClick,
  btnText = 'Reload Page',
  message = 'Taking longer than expected? Try reloading the page.',
  showButtonAfterMs = env.general.reloadPageTimeoutMs,
  children,
  ...props
}: LoadingPageProps) {
  const [showReloadButton, setShowReloadButton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowReloadButton(true);
    }, showButtonAfterMs);

    return () => clearTimeout(timer);
  }, [showButtonAfterMs]);

  const handleHardReload = () => {
    if (onClick) {
      onClick();
      return;
    }

    // Default behavior: Clear cache and cookies before reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies (if any)
    document.cookie.split(';').forEach((c) => {
      const eqPos = c.indexOf('=');
      const name = eqPos > -1 ? c.substr(0, eqPos) : c;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });

    // Hard reload the page
    window.location.reload();
  };

  return (
    <ContentContainer data-testid="loading-page" center {...props}>
      <Stack spacing={3} alignItems="center" data-testid="loading-content">
        {children || <CircularProgress size={60} data-testid="loading-spinner" />}
        {showReloadButton && (
          <Stack spacing={2} alignItems="center" data-testid="reload-section">
            <Typography variant="body2" color="text.secondary" textAlign="center" data-testid="reload-message">
              {message}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleHardReload}
              data-testid="reload-button"
              sx={{
                backgroundColor: '#1B263B',
                '&:hover': {
                  backgroundColor: '#FF6F59',
                },
              }}
            >
              {btnText}
            </Button>
          </Stack>
        )}
      </Stack>
    </ContentContainer>
  );
}
